// Branded loading screen: inlines the LMLM logo SVG and morphs its background circle into a
// gently pulsing blob with anime.js. Everything here is best-effort and self-contained:
//  - The SVG is imported as a raw string (Vite `?raw`) and injected into the DOM, because an
//    <img src=…svg> can't be animated — only inlined SVG nodes can.
//  - The logo's background is a <circle id/class "BG-CIRCLE-animate">; anime.js `svg.morphTo`
//    morphs <polygon> points, so we REPLACE that circle with a many-sided <polygon> (visually a
//    circle) and morph its points. Center/radius are taken from the SVG's real viewBox.
//  - prefers-reduced-motion, a missing element, or any anime.js failure all fall back to the
//    static logo (no throw); the caller's boot flow must never depend on this succeeding.
import logoSvgRaw from './lmlm-logo.svg?raw';

// The morphed circle lives in the SVG's own coordinate space (viewBox 0 0 1042.96 1042.96).
const CENTER_X = 521.48;
const CENTER_Y = 521.48;
const OUTER_R = 519; // matches the original circle radius (r=519.48), minus a hair for the stroke

/** A circle approximated as an N-gon (the morph baseline), in the SVG's coordinate space. */
function circlePoints(sides = 48): string {
  let points = '';
  for (let i = 0; i < sides; i++) {
    const a = (2 * Math.PI * i) / sides - Math.PI / 2;
    const x = CENTER_X + round(OUTER_R * Math.cos(a), 1);
    const y = CENTER_Y + round(OUTER_R * Math.sin(a), 1);
    points += `${x},${y} `;
  }
  return points.trim();
}

function round(n: number, dp: number): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

export interface LoadingAnimation {
  /** Stop the morph loop and release references. Safe to call multiple times. */
  destroy(): void;
}

/**
 * Inject the inline logo into `container` and start the morph loop. Returns a handle whose
 * destroy() stops the recursion. Never throws — on reduced-motion or any failure it leaves the
 * static logo in place and returns a no-op handle.
 */
export function mountLoadingLogo(container: HTMLElement): LoadingAnimation {
  // Strip the XML prolog / comments before the root <svg> so innerHTML parses cleanly as HTML.
  container.innerHTML = logoSvgRaw.replace(/^[\s\S]*?(?=<svg)/i, '');
  const svgEl = container.querySelector('svg');
  const noop: LoadingAnimation = { destroy() {} };
  if (!svgEl) return noop;

  // Replace the <circle> with a morphable <polygon> carrying the same class (so it keeps the
  // green stroke), filled pure black so the inside of the morphing circle reads solid black.
  const group = svgEl.querySelector('#BG-CIRCLE-animate');
  const circle = group?.querySelector('circle');
  if (!group || !circle) return noop;

  const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
  poly.setAttribute('class', circle.getAttribute('class') ?? 'stBG-CIRCLE-animate');
  poly.setAttribute('fill', '#000000');
  poly.setAttribute('points', circlePoints());
  // A hidden sibling polygon holds each next target shape for morphTo to read.
  const polyTarget = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
  polyTarget.setAttribute('points', circlePoints());
  polyTarget.style.display = 'none';
  group.replaceChild(poly, circle);
  group.appendChild(polyTarget);

  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) return noop; // static ring, no morph

  let stopped = false;
  let cleanup: (() => void) | null = null;

  // Load anime.js dynamically so a resolve/runtime failure can't break boot. The morph loop
  // recurses via onComplete; `stopped` halts it and destroy() best-effort cancels the running tween.
  void import('animejs')
    .then(({ animate, svg, utils }) => {
      if (stopped) return;

      const randomBlob = (): string => {
        // Vary the radius between an inner and the outer radius around the ring, like the anime.js
        // demo, but scaled to this logo's size for a subtle organic wobble.
        const sides = utils.random(14, 30);
        const total = sides % 2 ? sides + 1 : sides;
        const rInner = utils.random(Math.round(OUTER_R * 0.8), Math.round(OUTER_R * 0.93));
        let points = '';
        for (let i = 0; i < total; i++) {
          const r = i % 2 ? rInner : OUTER_R;
          const a = (2 * Math.PI * i) / total - Math.PI / 2;
          const x = CENTER_X + utils.round(r * Math.cos(a), 0);
          const y = CENTER_Y + utils.round(r * Math.sin(a), 0);
          points += `${x},${y} `;
        }
        return points.trim();
      };

      let current: ReturnType<typeof animate> | null = null;
      const step = () => {
        if (stopped) return;
        try {
          utils.set(polyTarget, { points: randomBlob() });
          current = animate(poly, {
            points: svg.morphTo(polyTarget),
            ease: 'inOutCirc',
            duration: 700,
            onComplete: step,
          });
        } catch {
          stopped = true; // give up quietly; the static ring remains
        }
      };
      cleanup = () => current?.pause();
      step();
    })
    .catch(() => {
      /* anime.js failed to load — keep the static ring, never block boot */
    });

  return {
    destroy() {
      stopped = true;
      try {
        cleanup?.();
      } catch {
        /* ignore */
      }
      cleanup = null;
    },
  };
}
